import React, {useState} from 'react'
import {Dropdown} from 'rsuite'
import {IComponent, ISection} from '../../../interfaces/Analytics/Analytics'
import {useIntl} from 'react-intl'
import ComponentAnalytics from '../Component/ComponentAnalytics'
import {useAnalyticsStore} from '../../../stores/Analytics/AnalyticStore'
import useUser from '../../../hooks/User'
import useAnalyticGuard from '../../../functions/Guard/analyticGuard'
import {useParams} from 'react-router-dom'
import {
  AnimateLayoutChanges,
  defaultAnimateLayoutChanges,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable'
import {CSS} from '@dnd-kit/utilities'
import {useDroppable} from '@dnd-kit/core'
import clsx from 'clsx'
import { MutableRefObject, Ref, useCallback } from 'react'


interface ISectionAnalytics {
  handleEditSection: (section: ISection) => void
  menu: ISection
  handleDeleteSection: (section: ISection) => void
  handleAddComponent: (section: ISection) => void
  components: IComponent[]
}

export default function SectionAnalytics({
  handleEditSection,
  menu,
  handleDeleteSection,
  handleAddComponent,
  components,
}: ISectionAnalytics) {
   function useCombinedRefs<T>(...refs: (Ref<T> | undefined)[]): (node: T | null) => void {
    return useCallback(
      (node: T | null) => {
        for (const ref of refs) {
          if (typeof ref === 'function') {
            ref(node)
          } else if (ref && typeof ref === 'object') {
            (ref as MutableRefObject<T | null>).current = node
          }
        }
      },
      [refs]
    )
  }
  const intl = useIntl()
  const user = useUser()
  const {isCanEditTemplate, isOwnerTemplate} = useAnalyticGuard(user)
  const {templateAnalyticId} = useParams()
  const {templateAnalytic, issectionDragging} = useAnalyticsStore()
  const animateLayoutChanges: AnimateLayoutChanges = (args) =>
    defaultAnimateLayoutChanges({...args, wasDragging: true})
  const {attributes, listeners, setNodeRef, transition, transform,isDragging} = useSortable({
    id: menu.sectionId,
    data: {
      type: 'section',
    },
    animateLayoutChanges,
  })
  const {setNodeRef: setNodeEmptyContainerRef, isOver: isEmptyContainerOver} = useDroppable({
    id: menu.id,
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const renderIconButtonSection = (props: any, ref: any) => {
    return (
      <button {...props} ref={ref} className='btn btn-sm btn-icon btn-light'>
        <i className={`fas fs-8 fa-ellipsis-v`}></i>
      </button>
    )
  }
  const [showComponent, setShowComponent] = useState(true)
  const handleShowComponent = () => {
    setShowComponent(!showComponent)
  }

  const {iscomponentsLoading} = useAnalyticsStore()
  const combinedRef = useCombinedRefs<HTMLDivElement>(setNodeRef, setNodeEmptyContainerRef)
  return (
    <div style={style}>
      <div className='card mt-5'>
        <div className='card-body py-3 px-5 d-flex justify-content-between align-items-center'>
          <span className='text-gray-800 fs-3 fw-bolder'>{menu.name}</span>
          <div className='d-flex gap-2 align-items-center'>
            {(isCanEditTemplate(templateAnalyticId || '') ||
              (templateAnalytic && isOwnerTemplate(templateAnalytic))) && (
              <button
                className='btn btn-sm btn-light me-2'
                data-kt-menu-trigger='click'
                data-kt-menu-placement='bottom-end'
                onClick={() => handleAddComponent(menu)}
              >
                <i className='fas fa-plus'></i> {intl.formatMessage({id: 'FORM.ACTION.ADD'})}{' '}
                {intl.formatMessage({id: 'FORM.LABEL.COMPONENT'})}
              </button>
            )}
            <button className='btn btn-sm btn-icon btn-light' onClick={handleShowComponent}>
              {showComponent && !issectionDragging ? (
                <i className='fa fa-eye' aria-hidden='true'></i>
              ) : (
                <i className='fa fa-eye-slash' aria-hidden='true'></i>
              )}
            </button>
            {isCanEditTemplate(templateAnalyticId || '') && (
              <Dropdown renderToggle={renderIconButtonSection} placement='bottomEnd'>
                <Dropdown.Item
                  onClick={() => {
                    handleEditSection(menu)
                  }}
                >
                  {intl.formatMessage({id: 'FORM.ACTION.UPDATE'})}
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => {
                    handleDeleteSection(menu)
                  }}
                >
                  {intl.formatMessage({id: 'FORM.ACTION.DELETE'})}
                </Dropdown.Item>
              </Dropdown>
            )}
            <div
              className='btn btn-sm btn-icon btn-light'
              ref={combinedRef}
              {...attributes}
              {...listeners}
            >
              <i className='bi bi-grip-vertical'></i>
            </div>
          </div>
        </div>
      </div>
      <div>
        <div className='row py-3 px-5'>
          {iscomponentsLoading && showComponent && (
            <div className='loading-overlay' style={{position: 'unset', background: 'unset'}}>
              <div
                className='loader'
                style={{border: '8px solid #ffffff', borderTop: '8px solid #3498db'}}
              ></div>
            </div>
          )}
          {showComponent && !iscomponentsLoading && !issectionDragging && (
            <SortableContext items={components}>
              {components.map((component, index) => (
                  <ComponentAnalytics component={component} section={menu} />
              ))}
            </SortableContext>
          )}
          {!iscomponentsLoading && components.length <= 0 && showComponent && !issectionDragging && (
            <div
              ref={!issectionDragging ? combinedRef : undefined}
              className={clsx('card p-8')}
              style={{ minHeight: '50vh' }}
            >
              <div className='w-100 h-100 d-flex justify-content-center align-items-center gap-4 flex-column'>
                <span style={{ fontSize: '1.5rem', color: '#A1A5B7' }}>
                  {intl.formatMessage(
                    { id: 'FORM.CONFIRM.DATA_EMPTY' },
                    {
                      title: intl.formatMessage({ id: 'MENU.ANALYTICS' }),
                    }
                  )}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
